import {
  isQuotationExpired,
  orderProgress,
  type DocLineInput,
  type InvoicedByLine,
  type SalesOrderLineRow as CoreLine,
  type SalesOrderRow as CoreOrder,
} from '@ims/core'
import type { Repository, SalesOrderLineRow, SalesOrderRow } from '@ims/data'
import type { CartLine } from './cart.ts'
import { daysBetween, dueWords, type Standing } from './purchases.ts'

// Quotations and sales orders in the app: rows as core's builders take them,
// the words for a document's state, and its lines as a quick-bill cart (the
// editor and "make a bill from it" are both quick bill with the cart filled in).

export type SalesOrderKind = SalesOrderRow['kind']

/** The row as clients write it (server-set columns dropped). */
export function salesOrderRow(row: SalesOrderRow): CoreOrder {
  const { created_by: _by, server_updated_at: _at, ...rest } = row
  return rest
}

export function salesOrderLineRows(rows: readonly SalesOrderLineRow[]): CoreLine[] {
  return rows.map(({ server_updated_at: _at, ...rest }) => rest)
}

export const KIND_WORDS = {
  quotation: { one: 'Quotation', many: 'Quotations', path: '/quotations', doc: 'quotation' },
  order: { one: 'Order', many: 'Orders', path: '/orders', doc: 'sales_order' },
} as const

export function salesOrderPath(order: Pick<SalesOrderRow, 'id' | 'kind'>): string {
  return `${KIND_WORDS[order.kind].path}/${order.id}`
}

export type StateTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

/**
 * A document's state in one badge. What a person decided is stored (sent,
 * accepted…); "Expired" comes from the date and "Billed" from the bills made
 * from it, so neither can go stale.
 */
export function salesOrderState(
  order: Pick<SalesOrderRow, 'kind' | 'status' | 'valid_until' | 'number'>,
  todayIso: string,
  billed?: { lines: readonly Pick<SalesOrderLineRow, 'id' | 'qty_milli'>[]; by: InvoicedByLine },
): { label: string; tone: StateTone } {
  const progress = billed ? orderProgress(billed.lines, billed.by).progress : 'none'
  switch (order.status) {
    case 'draft':
      return { label: 'Draft', tone: 'neutral' }
    case 'declined':
      return { label: 'Declined', tone: 'danger' }
    case 'cancelled':
      return { label: 'Cancelled', tone: 'danger' }
    case 'closed':
      return { label: 'Closed', tone: 'neutral' }
    default:
      break
  }
  if (!order.number) return { label: 'Syncing', tone: 'warning' }
  if (progress === 'full' || progress === 'over') return { label: 'Billed', tone: 'success' }
  if (progress === 'part') return { label: 'Part billed', tone: 'warning' }
  if (isQuotationExpired(order, todayIso)) return { label: 'Expired', tone: 'warning' }
  if (order.status === 'accepted') return { label: 'Accepted', tone: 'success' }
  return order.status === 'sent'
    ? { label: 'Sent', tone: 'info' }
    : { label: 'Confirmed', tone: 'info' }
}

/** The date `days` after an ISO date (a quotation's "valid until"). */
export function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10)
}

/**
 * Lines of a quotation or order as quick-bill cart lines. Prices and discounts
 * are the agreed ones, so they are never re-priced by a price tier. `forBill`:
 * the lines will be billed, so batches and serials are asked for as usual; an
 * editor of the document itself never needs them.
 */
export async function salesOrderCartLines(
  repo: Repository,
  lines: readonly DocLineInput[],
  forBill: boolean,
): Promise<Omit<CartLine, 'key'>[]> {
  const out: Omit<CartLine, 'key'>[] = []
  for (const line of lines) {
    const product = line.productId ? await repo.get('products', line.productId) : null
    const unit = product?.unit_id ? await repo.get('units', product.unit_id) : null
    out.push({
      ...line,
      discountPaisa: line.discountPaisa ?? 0,
      trackStock: product?.track_stock ?? false,
      unitSymbol: unit?.symbol ?? '',
      allowsDecimal: unit?.allows_decimal ?? false,
      mrpPaisa: product?.mrp_paisa ?? null,
      trackBatches: forBill && (product?.track_batches ?? false),
      trackSerials: forBill && (product?.track_serials ?? false),
      retailPricePaisa: product?.sell_price_paisa,
      wholesalePricePaisa: product?.wholesale_price_paisa ?? null,
      minPricePaisa: product?.min_price_paisa ?? null,
      fixedPrice: true,
    })
  }
  return out
}

/** What the list knows about a quotation or order: the row and what has been billed. */
export interface SalesOrderBoard {
  order: SalesOrderRow
  lineCount: number
  orderedMilli: number
  billedMilli: number
  unbilledPaisa: number
  madeOrder: boolean
}

/** The badge of a document in the list, from the summed quantities. */
export function salesBoardState(
  row: SalesOrderBoard,
  todayIso: string,
): { label: string; tone: StateTone } {
  const { order, orderedMilli, billedMilli } = row
  const base = salesOrderState(order, todayIso)
  const live = ['sent', 'accepted', 'confirmed'].includes(order.status) && !!order.number
  if (!live || billedMilli === 0) return base
  return billedMilli < orderedMilli
    ? { label: 'Part billed', tone: 'warning' }
    : { label: 'Billed', tone: 'success' }
}

/** A quotation is "running out" when it is valid for this many more days or fewer. */
export const QUOTE_SOON_DAYS = 3

/**
 * Where a quotation or sales order stands, in two short lines. `show` formats a date
 * and `amount` money in paisa.
 */
export function salesOrderStanding(
  row: SalesOrderBoard,
  today: string,
  show: (iso: string) => string,
  amount: (paisa: number) => string,
): Standing {
  const { order, lineCount, billedMilli, orderedMilli, unbilledPaisa, madeOrder } = row
  const items = `${lineCount} item${lineCount === 1 ? '' : 's'}`
  const reason = order.close_reason ?? undefined
  if (order.status === 'draft') return { main: 'Not sent yet', sub: items }
  if (order.status === 'declined') return { main: 'Said no', sub: reason }
  if (order.status === 'cancelled') return { main: 'Cancelled', sub: reason }
  if (order.status === 'closed') return { main: 'Closed', sub: reason }
  if (madeOrder) return { main: 'Made into an order', sub: items }
  if (billedMilli > 0 && billedMilli >= orderedMilli)
    return { main: 'All billed', sub: items, tone: 'success' }
  if (billedMilli > 0)
    return {
      main: `${amount(order.total_paisa - unbilledPaisa)} billed`,
      sub: `${amount(unbilledPaisa)} left`,
    }
  if (order.kind === 'order') {
    if (!order.expected_date) return { main: 'Nothing billed yet', sub: items }
    const due = dueWords(order.expected_date, today, show, 'Expected')
    return {
      main: 'Nothing billed yet',
      sub: due.text,
      ...(due.late ? { tone: 'danger', toneOn: 'sub' } : {}),
    }
  }
  if (isQuotationExpired(order, today))
    return { main: `Ran out ${show(order.valid_until!)}`, sub: 'No answer', tone: 'warning' }
  const valid = order.valid_until ? `Valid till ${show(order.valid_until)}` : items
  if (order.status === 'accepted') return { main: 'Ready to bill', sub: valid, tone: 'success' }
  if (!order.valid_until) return { main: 'No answer yet', sub: items }
  const left = daysBetween(today, order.valid_until)
  if (left > QUOTE_SOON_DAYS) return { main: valid, sub: 'No answer yet' }
  return {
    main: left === 0 ? 'Valid until today' : `Valid ${left} more day${left === 1 ? '' : 's'}`,
    sub: 'No answer yet',
    tone: 'warning',
  }
}

/**
 * How long a date is away, for the numbers at the top of a document's page: "4 days left",
 * "Last day", "Expired" (`word` 'left'), or "In 3 days", "Today", "2 days late" ('due').
 * `pct` is how much of the time from `from` has gone, for a bar.
 */
export function countdown(
  from: string,
  until: string,
  today: string,
  word: 'left' | 'due',
): { text: string; tone: 'neutral' | 'warning' | 'danger'; pct: number } {
  const days = daysBetween(today, until)
  const span = Math.max(1, daysBetween(from, until))
  const pct = Math.max(0, Math.min(100, Math.round(((span - days) / span) * 100)))
  const plural = (n: number) => `${n} day${n === 1 ? '' : 's'}`
  if (days < 0)
    return { text: word === 'left' ? 'Expired' : `${plural(-days)} late`, tone: 'danger', pct: 100 }
  if (days === 0) return { text: word === 'left' ? 'Last day' : 'Today', tone: 'warning', pct }
  const tone = days <= 3 ? 'warning' : 'neutral'
  return { text: word === 'left' ? `${plural(days)} left` : `In ${plural(days)}`, tone, pct }
}

export type StepState = 'done' | 'now' | 'later' | 'stopped'

export interface QuotationStep {
  key: 'saved' | 'sent' | 'answer' | 'next'
  title: string
  state: StepState
  /** One short line under the title. */
  note: string
}

/**
 * Where a quotation stands, as four steps (board DocQuote): saved and numbered, sent, the
 * customer's answer, then an order or a bill. Worked out from its status and what was made
 * from it; nothing is stored.
 */
export function quotationSteps(
  order: Pick<SalesOrderRow, 'status' | 'number'>,
  made: { order: boolean; billed: boolean; expired: boolean },
): QuotationStep[] {
  const s = order.status
  const draft = s === 'draft'
  const cancelled = s === 'cancelled'
  const answered = s === 'accepted' || s === 'declined' || made.order || made.billed
  const sent = !draft && (s !== 'cancelled' || answered)
  const next = made.order || made.billed
  return [
    {
      key: 'saved',
      title: draft ? 'Draft' : 'Saved and numbered',
      state: draft ? 'now' : 'done',
      note: draft ? 'Not numbered yet' : (order.number ?? 'Number on sync'),
    },
    {
      key: 'sent',
      title: 'Sent',
      state: draft ? 'later' : cancelled && !sent ? 'stopped' : 'done',
      note: draft
        ? 'Mark it sent when it goes to the customer'
        : cancelled
          ? 'Cancelled'
          : 'Sent to the customer',
    },
    {
      key: 'answer',
      title: "The customer's answer",
      state:
        s === 'declined' || (cancelled && !answered)
          ? 'stopped'
          : answered
            ? 'done'
            : s === 'sent'
              ? made.expired
                ? 'stopped'
                : 'now'
              : 'later',
      note:
        s === 'declined'
          ? 'They said no'
          : answered
            ? 'They said yes'
            : cancelled
              ? 'Cancelled'
              : s === 'sent'
                ? made.expired
                  ? 'No answer before the prices ran out'
                  : 'Waiting. Mark it when they reply'
                : 'After it is sent',
    },
    {
      key: 'next',
      title: 'Ordered or billed',
      state: next
        ? 'done'
        : s === 'accepted'
          ? 'now'
          : s === 'declined' || cancelled
            ? 'stopped'
            : 'later',
      note: made.order
        ? 'An order was made from it'
        : made.billed
          ? 'Billed'
          : s === 'accepted'
            ? 'Make an order, or bill it straight away'
            : 'Not yet',
    },
  ]
}
